import type { LocationQuery, LocationQueryValue } from 'vue-router';

/**
 * Editor 路由入口来源查询参数键。
 *
 * 说明：
 * 1. 只用于宿主层区分“同一个 editor 路由”是从哪条入口链路进入；
 * 2. 不承载业务数据，只承载初始化行为所需的入口语义；
 * 3. 采用 query 而非全局单例，保证刷新后仍可重建相同初始化结果。
 */
export const EDITOR_ENTRY_SOURCE_QUERY_KEY = 'editorEntrySource';

/**
 * Editor 路由入口来源。
 */
export enum EditorEntrySource {
  /** 开发环境自动直达项目入口。 */
  DevAutoOpenProject = 'dev-auto-open-project',
}

//------ 编辑器路由文档类型识别 开始------
/**
 * Editor 路由中可直接识别的 CAD 文档类型。
 */
export enum EditorRouteDocumentType {
  /** 路由明确指向零件文档。 */
  Part = 'part',
  /** 路由明确指向装配体文档。 */
  Assembly = 'assembly',
  /** 路由未携带可判定的 CAD 文档类型。 */
  Unknown = 'unknown',
}

/**
 * 编辑器顶部工具栏可消费的文档标签类型。
 */
export type EditorActiveTabType = 'assembly' | 'part' | null;
//------ 编辑器路由文档类型识别 结束------

/**
 * 规范化 query 中的单值字符串。
 *
 * @param value query 原始值。
 * @returns 单个字符串值；不存在时返回 null。
 */
function normalizeQueryValue(value: LocationQueryValue | LocationQueryValue[]): string | null {
  if (Array.isArray(value)) {
    return typeof value[0] === 'string' ? value[0] : null;
  }
  return typeof value === 'string' ? value : null;
}

/**
 * 判断当前 Editor 路由是否来自开发环境自动直达项目入口。
 *
 * @param query 当前路由 query。
 * @returns 是否来自 VITE_DEV_AUTO_OPEN_PROJECT_ID 对应的开发入口。
 */
export function isDevAutoOpenProjectEditorEntry(query: LocationQuery): boolean {
  const source = normalizeQueryValue(query[EDITOR_ENTRY_SOURCE_QUERY_KEY]);
  return source === EditorEntrySource.DevAutoOpenProject;
}

/**
 * 判断当前 projectId 是否命中开发环境自动直达项目配置。
 *
 * @param projectId 当前 editor 路由中的项目 ID。
 * @param configuredProjectId 环境变量配置的自动直达项目 ID。
 * @returns 是否命中 VITE_DEV_AUTO_OPEN_PROJECT_ID。
 */
export function isDevAutoOpenProjectIdMatched(
  projectId: string,
  configuredProjectId: string,
): boolean {
  const normalizedProjectId = projectId.trim();
  const normalizedConfiguredProjectId = configuredProjectId.trim();
  if (!normalizedProjectId || !normalizedConfiguredProjectId) {
    return false;
  }
  return normalizedProjectId === normalizedConfiguredProjectId;
}

//------ 编辑器路由文档类型识别 开始------
/**
 * 从 Editor 路由 projectId 中解析 CAD 文档类型。
 *
 * 设计说明：
 * 1. 开发入口和直接访问共用同一条 editor 路由，文档类型必须来自路由本身，而不是入口来源；
 * 2. 当前本地开发路由会把尾部 `/` 编码为 `%2F`，Vue Router params 中通常已解码，这里只归一化尾斜杠表达；
 * 3. 仅识别稳定的 CAD 源文件后缀，未知格式交给加载后的标签语义继续兜底。
 *
 * @param projectId 当前 editor 路由中的项目 ID。
 * @returns 路由中可识别的 CAD 文档类型。
 */
export function resolveEditorRouteDocumentType(projectId: string): EditorRouteDocumentType {
  const normalizedLowerProjectId = projectId
    .trim()
    .replace(/%2f/giu, '/')
    .replace(/\\/g, '/')
    .replace(/\/+$/u, '')
    .toLowerCase();

  if (normalizedLowerProjectId.endsWith('.assembly.cad')) {
    return EditorRouteDocumentType.Assembly;
  }

  if (normalizedLowerProjectId.endsWith('.part') || normalizedLowerProjectId.endsWith('.part.cad')) {
    return EditorRouteDocumentType.Part;
  }

  return EditorRouteDocumentType.Unknown;
}

/**
 * 解析编辑器当前应展示的工具栏标签类型。
 *
 * 设计说明：
 * 1. `.assembly.cad` / `.part.cad` 直达路由本身就是权威 CAD 文档语义；
 * 2. GLB 标签类型在加载完成前可能仍为 `null`，此时不能让 UI 暂时退回零件工具栏；
 * 3. 未知路由继续交给已加载标签类型兜底，保持非标准入口的兼容行为。
 *
 * @param projectId 当前 editor 路由中的项目 ID。
 * @param loadedTabType 已加载标签解析出的类型。
 * @returns UI 应使用的文档标签类型。
 */
export function resolveEditorActiveTabType(
  projectId: string,
  loadedTabType: EditorActiveTabType,
): EditorActiveTabType {
  const routeDocumentType = resolveEditorRouteDocumentType(projectId);

  if (routeDocumentType === EditorRouteDocumentType.Assembly) {
    return 'assembly';
  }

  if (routeDocumentType === EditorRouteDocumentType.Part) {
    return 'part';
  }

  return loadedTabType;
}
//------ 编辑器路由文档类型识别 结束------
